import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';
import { NavigateFunction } from 'react-router-dom';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    navigate: NavigateFunction;
    receiverId: string | null;
    setReceiverId: (value: string | null) => void;
    supplier: string | null;
    setSupplier: (value: string | null) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useEnterReceiverSupplier = (props: { wizard_id: string }): ViewProps => {
    const { navigate, store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const { syncTransaction } = useWizardTransaction({ wizardId: props.wizard_id, store });
    const [existingReceiver, setExistingReceiver] = useState<TransactionUser | null>(store?.data.receiver || null);
    const [receiverId, setReceiverId] = useState<string | null>(store?.data.receiverId || store?.data.receiver?.id || null);
    const [supplier, setSupplier] = useState<string | null>(store?.data.supplier || store?.data.supplier?.name || null);

    useEffect(() => {
        if (store?.data.receiver) {
            setExistingReceiver(store.data.receiver);
            setReceiverId(store.data.receiverId || store.data.receiver.user_id);
        } else if (store?.data.receivers?.length) {
            const receiver = store.data.receivers[0];
            setExistingReceiver(receiver);
            setReceiverId(receiver.user_id);
        }

        if (store?.data.supplier) {
            setSupplier(typeof store.data.supplier === 'string' ? store.data.supplier : store.data.supplier.name || null);
        }

        if (store?.data.receiver || store?.data.receivers?.length || !store?.data.transaction?.id) return;

        TransactionUserClient.fetchTransactionUsers({ transaction_id: store.data.transaction.id }).then((users) => {
            const rec = users.results.find((user) => user.type === 'receiver');
            if (rec) {
                setExistingReceiver(rec);
                setReceiverId(rec.user_id);
            }
        });
    }, [store?.data.receiver, store?.data.receivers, store?.data.supplier, store?.data.transaction?.id]);

    const onSubmit = async () => {
        if (!props.wizard_id || !store) return;
        if (!receiverId) return;

        const newTransaction = await syncTransaction({ supplier: store.data.transaction.supplier });

        let receiver = existingReceiver;
        if (!existingReceiver || (existingReceiver && existingReceiver.user_id !== receiverId)) {
            if (existingReceiver) await TransactionUserClient.deleteTransactionUser(newTransaction.account_id, newTransaction.id, existingReceiver.id);
            receiver = await TransactionUserClient.postTransactionUser(newTransaction.account_id, newTransaction.id, {
                user_id: receiverId,
                type: TransactionUserType.RECEIVER
            });
        }

        updateWizardData({
            receivers: [receiver],
            receiverId,
            supplier,
            transaction: newTransaction
        });

        const nextStep = store?.steps.find((step) => step.screen === ScreenType.ENTER_RECEIVER_SUPPLIER && step.trigger === ScreenTrigger.SUBMIT);
        navigateToNextStep(nextStep);
    };

    return {
        navigate,
        receiverId,
        setReceiverId,
        supplier,
        setSupplier,
        submitDisabled: !receiverId || !store?.data.transaction,
        onSubmit,
        onBack
    };
};

export default useEnterReceiverSupplier;
